import type { ReactNode } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { hasRole, hasAnyRole } from '../../utils/permissions';
import UnauthorizedPage from './UnauthorizedPage';

interface RoleGateProps {
  /** Single role key required */
  requires?: string;
  /** Any of these role keys grants access */
  anyOf?: string[];
  /** Require admin flag */
  adminOnly?: boolean;
  /**
   * Peak admin OR any of these role keys (e.g. delegated ``feature.imports`` without ``is_admin``).
   */
  adminOrAnyOf?: string[];
  children: ReactNode;
  /**
   * Element to render when the gate fails.
   * Defaults to <UnauthorizedPage /> for route-level guards.
   * Pass null to silently hide (original behaviour for UI-only gates).
   */
  fallback?: ReactNode;
}

export default function RoleGate({
  requires,
  anyOf,
  adminOnly,
  adminOrAnyOf,
  children,
  fallback = <UnauthorizedPage />,
}: RoleGateProps) {
  const { currentUser } = useAuth();

  if (!currentUser) return <>{fallback}</>;
  if (adminOnly && !currentUser.is_admin) return <>{fallback}</>;
  if (adminOrAnyOf?.length) {
    const allowed = currentUser.is_admin || hasAnyRole(currentUser, adminOrAnyOf);
    if (!allowed) return <>{fallback}</>;
  }
  if (requires && !hasRole(currentUser, requires)) return <>{fallback}</>;
  if (anyOf && !hasAnyRole(currentUser, anyOf)) return <>{fallback}</>;

  return <>{children}</>;
}
